import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { MonitoredServer } from "@xop/dashboard-shared";
import type { JSX, ReactNode } from "react";

import type { ServerTone } from "../serverHealth.js";

/**
 * Tone → colour mapping, the reachability / SSL pill text + tone helpers, and
 * the small `<Pill>` chip — extracted from `ServerList.tsx`. `ServerTone` is
 * the shared type from `serverHealth.ts` (it used to be re-declared locally).
 * The pill text/tone functions are pure (server → string / tone), so they
 * unit-test directly.
 */

/** Map a tone onto a theme palette colour for fills / accents. */
export function toneColor(t: Theme, tone: ServerTone): string {
  switch (tone) {
    case "good":
      return t.palette.success.main;
    case "warn":
      return t.palette.warning.main;
    case "bad":
      return t.palette.error.main;
    default:
      return t.palette.text.disabled;
  }
}

export function onlinePill(server: MonitoredServer): string {
  if (server.reachable === null) return "checking";
  if (server.reachable)
    return server.http_status != null ? `HTTP ${server.http_status}` : "up";
  return "down";
}

export function onlineTone(server: MonitoredServer): ServerTone {
  if (server.reachable === null) return "muted";
  return server.reachable ? "good" : "bad";
}

export function sslTone(server: MonitoredServer): ServerTone {
  if (!server.ssl) return "muted";
  if (!server.ssl.valid) return "bad";
  if (server.ssl.days_remaining != null && server.ssl.days_remaining < 30)
    return "warn";
  return "good";
}

export function sslPill(server: MonitoredServer): string {
  if (!server.ssl) return server.url.startsWith("https://") ? "SSL ?" : "SSL n/a";
  if (!server.ssl.valid) return "SSL bad";
  if (server.ssl.days_remaining == null) return "SSL ok";
  return `SSL ${server.ssl.days_remaining}d`;
}

export function Pill({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: ServerTone;
}): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        borderRadius: 999,
        // `surfaceSoft` is a custom palette key (= bgSoft) the app's theme
        // supplies; fall back to the default surface under a bare MUI theme
        // (e.g. a unit test without the app ThemeProvider) so `alpha()`
        // never receives `undefined` and crashes the whole card grid.
        bgcolor: (t) => alpha(t.palette.surfaceSoft ?? t.palette.background.default, 0.7),
        px: 0.75,
        py: 0.25,
        fontFamily: (t) => t.typography.fontFamily,
        fontSize: 10,
        fontWeight: 500,
        color: tone === "muted" ? "text.disabled" : (t) => toneColor(t, tone),
      }}
    >
      {children}
    </Box>
  );
}
